import React, { useState } from 'react';
import { useAppStore } from '@/lib/store';
import { Plus, Trash2, Edit2, Check, X, Search, Book } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const AuthorDictionary: React.FC = () => {
    const { activeNovelId, novels, addCustomWord, removeCustomWord, updateCustomWord, themeProfile } = useAppStore();
    const activeNovel = novels.find(n => n.id === activeNovelId);
    const customWords = activeNovel?.customWords || [];

    const [isAdding, setIsAdding] = useState(false);
    const [editingWord, setEditingWord] = useState<string | null>(null);
    const [newWord, setNewWord] = useState('');
    const [newDefinition, setNewDefinition] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const handleAdd = () => {
        if (newWord.trim() && activeNovelId) {
            addCustomWord(activeNovelId, newWord.trim(), newDefinition.trim());
            setNewWord('');
            setNewDefinition('');
            setIsAdding(false);
        }
    };

    const handleUpdate = (word: string) => {
        if (activeNovelId) {
            updateCustomWord(activeNovelId, word, newDefinition.trim());
            setEditingWord(null);
            setNewDefinition('');
        }
    };

    const startEditing = (word: string, currentDef: string) => {
        setEditingWord(word);
        setNewDefinition(currentDef);
    };

    const filteredWords = customWords.filter(cw =>
        cw.word.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cw.definition.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="flex flex-col h-full bg-transparent overflow-hidden">
            <div className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: themeProfile.text }}>
                        <Book className="w-5 h-5" style={{ color: themeProfile.accent }} />
                        Author's Dictionary
                    </h3>
                    <button
                        onClick={() => setIsAdding(true)}
                        className="p-2 rounded-full transition-colors shadow-lg hover:scale-105"
                        style={{ backgroundColor: themeProfile.accent, color: themeProfile.background }}
                    >
                        <Plus className="w-5 h-5" />
                    </button>
                </div>

                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: themeProfile.text, opacity: 0.5 }} />
                    <input
                        type="text"
                        placeholder="Search custom words..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 rounded-xl border-2 outline-none transition-colors backdrop-blur-sm"
                        style={{
                            backgroundColor: themeProfile.paperSurface,
                            color: themeProfile.text,
                            borderColor: themeProfile.border,
                        }}
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-3 custom-scrollbar">
                <AnimatePresence mode="popLayout">
                    {isAdding && (
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="p-4 rounded-2xl border-2 shadow-sm space-y-3"
                            style={{
                                backgroundColor: themeProfile.paperSurface,
                                borderColor: themeProfile.accent,
                            }}
                        >
                            <input
                                autoFocus
                                type="text"
                                placeholder="Word..."
                                value={newWord}
                                onChange={(e) => setNewWord(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border outline-none"
                                style={{ backgroundColor: themeProfile.background, color: themeProfile.text, borderColor: themeProfile.border }}
                            />
                            <textarea
                                placeholder="Definition or notes..."
                                value={newDefinition}
                                onChange={(e) => setNewDefinition(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border outline-none resize-none h-20"
                                style={{ backgroundColor: themeProfile.background, color: themeProfile.text, borderColor: themeProfile.border }}
                            />
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => setIsAdding(false)}
                                    className="px-3 py-1.5 rounded-lg transition-colors hover:scale-105"
                                    style={{ color: themeProfile.text, opacity: 0.7 }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAdd}
                                    className="px-3 py-1.5 rounded-lg transition-colors hover:scale-105"
                                    style={{ backgroundColor: themeProfile.accent, color: themeProfile.background }}
                                >
                                    Add Word
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {filteredWords.length === 0 && !isAdding ? (
                        <div className="flex flex-col items-center justify-center py-12 space-y-2" style={{ color: themeProfile.text, opacity: 0.5 }}>
                            <Book className="w-12 h-12 opacity-50" />
                            <p>No custom words found</p>
                        </div>
                    ) : (
                        filteredWords.map((cw) => (
                            <motion.div
                                key={cw.word}
                                layout
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="p-4 rounded-2xl border shadow-sm hover:shadow-md transition-all group relative"
                                style={{ backgroundColor: themeProfile.paperSurface, borderColor: themeProfile.border }}
                            >
                                <div className="flex justify-between items-start mb-1">
                                    <h4 className="font-bold text-lg" style={{ color: themeProfile.text }}>{cw.word}</h4>
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {editingWord === cw.word ? (
                                            <>
                                                <button
                                                    onClick={() => handleUpdate(cw.word)}
                                                    className="p-1.5 rounded-lg hover:scale-110 transition-transform text-green-500"
                                                >
                                                    <Check className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => setEditingWord(null)}
                                                    className="p-1.5 rounded-lg hover:scale-110 transition-transform text-red-500"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => startEditing(cw.word, cw.definition)}
                                                    className="p-1.5 rounded-lg hover:scale-110 transition-transform"
                                                    style={{ color: themeProfile.accent }}
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => activeNovelId && removeCustomWord(activeNovelId, cw.word)}
                                                    className="p-1.5 rounded-lg hover:scale-110 transition-transform text-red-500"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {editingWord === cw.word ? (
                                    <textarea
                                        autoFocus
                                        value={newDefinition}
                                        onChange={(e) => setNewDefinition(e.target.value)}
                                        className="w-full px-2 py-1.5 rounded-lg border outline-none resize-none h-16 text-sm mt-2"
                                        style={{ backgroundColor: themeProfile.background, color: themeProfile.text, borderColor: themeProfile.accent }}
                                    />
                                ) : (
                                    <p className="text-sm leading-relaxed italic mt-1" style={{ color: themeProfile.text, opacity: 0.8 }}>
                                        {cw.definition || "No definition provided."}
                                    </p>
                                )}
                            </motion.div>
                        ))
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};
